import React, { useCallback, useState, useEffect } from "react";
import {loadStripe} from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
  useSearchParams
} from "react-router-dom";

import '../styles/PaymentPage.css'
import { updateColors, useTheme } from "../hooks/ThemeContext";
import { IndexHeader } from "./IndexHeader";
import * as Sentry from '@sentry/react';


const stripePromise = loadStripe("pk_live_51RWiNcBOoKsKuzAu50fYIUXYzYNcqGs5fgAJHN52GyrqcWqqsgpu1tSzRC5iqGMCMfEyJwj09GKKUFOFrrSXTFQR00N2yDLCkq");

const CheckoutForm = () => {
  const fetchClientSecret = useCallback(async () => {
    try {
      const response = await fetch("/api/payment", {
        method: "POST",
      });
      const data = await response.json();

      if (!response.ok || !data?.client_secret) {
        throw new Error(data?.error_message || `Failed to create checkout session (${response.status})`);
      }

      return data.client_secret;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }, []);

  const options = {fetchClientSecret};

  return (
    <div id="checkout">
      <EmbeddedCheckoutProvider
        stripe={stripePromise}
        options={options}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  )
}

function Payment() {
    const [currUser, setCurrentUser] = useState({});
    const [isIndexLoaded, setIsIndexLoaded] = useState(false); //backend response for index header complete or not
    const {setTheme} = useTheme();
    const [searchParams, setSearchParams] = useSearchParams();
    const [returnScreen, setReturnScreen] = useState(false);
    const [status, setStatus] = useState(null);
    const [customerEmail, setCustomerEmail] = useState('');
    const [returnWait, setReturnWait] = useState(false);

    const navigate = useNavigate();

    const userRefresh = async (redironupgrade = false) => {
      setIsIndexLoaded(false);
      try {
        const response = await fetch(`/api/index`);
        const data = await response.json();

        if (response.ok) {
          if (data.colors) {
            localStorage.setItem('theme-colors', JSON.stringify(data.colors));
            updateColors(data.colors, setTheme);
          }

          if (data.user) {
            setCurrentUser(data.user);
            if (redironupgrade && data.user.plan === "premium") {
              if (data.user.resume_file) {
                navigate("/results");
              } else {
                navigate("/index");
              }
            }
          } else {
            setCurrentUser({
              'na': true
            });
            navigate("/login");
          }

          if (data.error_message) {
            // doesn't really matter in this scenario
          }
        } else {
          setCurrentUser({ 'error': true });
          throw new Error(data?.error_message || `/api/index failed with status ${response.status}`);
        }
      } catch (error) {
        Sentry.captureException(error);
        setCurrentUser({ 'error': true });
      } finally {
        setIsIndexLoaded(true);
      }
    };

    useEffect(() => {
      const syncPaymentState = async () => {
        setIsIndexLoaded(false);
        const newReturnId = searchParams.get("return_id");

        if (!newReturnId) {
          setReturnScreen(false);
          setStatus(null);
          setCustomerEmail('');
          setReturnWait(false);
          await userRefresh();
          return;
        }

        setReturnScreen(true);
        setReturnWait(false);

        try {
          const response = await fetch(`/api/payment?return_id=${newReturnId}`);
          const data = await response.json();

          if (!response.ok) {
            throw new Error(data?.error_message || `Failed to load payment session (${response.status})`);
          }

          if (data.status === "open") {
            navigate("payment");
            return;
          }

          if (data.status === "DNE") {
            const nextSearchParams = new URLSearchParams(searchParams);
            nextSearchParams.delete('return_id');
            setSearchParams(nextSearchParams);
            return;
          }

          setStatus(data.status);
          setCustomerEmail(data.customer_email || '');
          setReturnWait(true);
          await userRefresh(data.status === "complete");
        } catch (error) {
          Sentry.captureException(error);
          setReturnWait(true);
          setStatus('error');
        }
      };

      syncPaymentState();
    }, [navigate, searchParams, setSearchParams]);
      
    return <>
    <header>
        <div className="top-header" id="top-header">
            <IndexHeader user={currUser} firstWait={isIndexLoaded}/>
        </div>
    </header>
    {returnScreen ?
    (returnWait && isIndexLoaded ?
    (status === 'open' ?
    /*User should not be here, should be renavigated*/
    <p>...</p>
    :
    (status === 'complete' ?    
    <section style={{textAlign: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column'}} id="success">
      {currUser.plan === "premium" ?
      <>
        <p>
          Success! You are now a premium user.
          </p>
        <p>
          If you have any questions, please email us!
        </p>
      </>
      :
        <>
        <p>
          We are currently processing your payment! A confirmation email will be sent to <span style={{color: 'var(--primary-aw)'}}>{customerEmail}</span>
          </p>
        <p>
          If you have any questions, please email us! Refresh the page to recheck your status.
        </p>
        </>
      }
      </section>
    :
    <p>Unknown Status: {status}</p>
    )
    )
    :
    <div className="spinner" style={{margin:'auto'}}></div>
    )
    :
    <CheckoutForm/>}
    </>;
}

export {Payment}